// --------------------------------------------------------------
// Created On: 2021-03-09
// Author: Zachary Thomas
//
// Last Modified: 2024-04-08
// Modified By: Zachary Thomas
//
// Copyright 2024 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

/// <reference types="google.maps" />
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { GoogleMap, useJsApiLoader, Polygon, Marker, InfoWindow } from "@react-google-maps/api";
import MapAssetAlertBanner from "./MapAssetAlertBanner/MapAssetAlertBanner";
import deepCopy from "../../../utilities/deepCopy";
import calculateCenterZoom from "../../../utilities/calculateCenterZoom";
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_DEFAULT_ZOOM,
  GOOGLE_MAPS_DEFAULT_LAT,
  GOOGLE_MAPS_DEFAULT_LNG,
  GOOGLE_MAPS_BOTTOM_LEFT,
  GOOGLE_MAPS_HORIZONTAL_BAR,
  GOOGLE_MAPS_ROAD_MAP,
  GOOGLE_MAPS_SATELLITE,
  GOOGLE_MAPS_MIN_ZOOM,
} from "../../../constants/googleMaps";
import isoUtcIsStale from "../../../utilities/time/isoUtcIsStale";
import styles from "./GroupMap.module.scss";

// Map that displays asset locations from an asset group.
export default function GroupMap(props: Props): Component {
  const [assets, setAssets] = useState<VerifiedMapAsset[]>([]);
  const [center, setCenter] = useState<ShortPoint>({
    lat: GOOGLE_MAPS_DEFAULT_LAT,
    lng: GOOGLE_MAPS_DEFAULT_LNG,
  });
  const [zoom, setZoom] = useState<number>(GOOGLE_MAPS_DEFAULT_ZOOM);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<VerifiedMapAsset | null>(null);
  const [markerMap, setMarkerMap] = useState<MarkerMap>({});
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  // Draw geofence polygons to the map.
  useEffect(() => {
    let newGeofencePolygon: google.maps.Polygon | null = null;
    if (map && props.geofencePoints && props.geofenceEnabled) {
      // Get the complete geofence path.
      const geofencePath = props.geofencePoints.map((geofencePoint) => {
        return { lat: geofencePoint.latitude, lng: geofencePoint.longitude };
      });

      // Draw the new geofence polygon.
      if (geofencePath.length > 0) {
        newGeofencePolygon = new google.maps.Polygon({
          paths: geofencePath,
          strokeColor: "#3957d9",
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: "#3957d9",
          fillOpacity: 0.35,
        });

        if (newGeofencePolygon !== null) {
          newGeofencePolygon.setMap(map);
        }
      }
    }

    // Clean up old polygons off the map.
    return () => {
      if (newGeofencePolygon !== null) {
        newGeofencePolygon.setMap(null);
      }
    };
  }, [map, props.geofenceEnabled, JSON.stringify(props.geofencePoints)]);

  // Get the required center and zoom level to fit all markers on the map.
  useEffect(() => {
    // Only include assets with valid location data.
    const tempAssets: VerifiedMapAsset[] = [];
    const tempPoints: Point[] = [];
    props.assets.forEach((asset) => {
      if (asset.latitude !== null && asset.longitude !== null) {
        tempAssets.push(deepCopy(asset as VerifiedMapAsset));
        tempPoints.push({
          latitude: asset.latitude,
          longitude: asset.longitude,
        });
      }
    });

    // Find the new bounding box that fits all markers.
    if (map !== null && tempPoints.length > 0) {
      const newBounds = calculateCenterZoom(tempPoints, map);
      setCenter(newBounds.center);
      setZoom(newBounds.zoom - 1);
    } else {
      setZoom(GOOGLE_MAPS_DEFAULT_ZOOM);
    }

    setAssets(tempAssets);
    setSelectedAsset(null);
  }, [JSON.stringify(props.assets)]);

  // Handel load map event.
  function handleLoadMap(map: google.maps.Map): void {
    const bounds = new window.google.maps.LatLngBounds(center);
    map.fitBounds(bounds);
    setMap(map);
  }

  // Handle load event of marker so that it aligns correctly with info windows.
  function handleMarkerLoad(marker: google.maps.Marker, asset: VerifiedMapAsset): void {
    setMarkerMap((prevState) => {
      return { ...prevState, [asset.assetId]: marker };
    });
  }

  // Handle selection of asset.
  function selectAsset(asset: VerifiedMapAsset): void {
    // Set the selected asset to null before setting it to a specific value.
    // This prevents an issue with the InfoWindow where the anchor is not updated
    // correctly.
    setSelectedAsset(null);
    setSelectedAsset(asset);
  }

  // Handel unmount map event.
  function handleUnmountMap(): void {
    setMap(null);
  }

  return (
    <div className={styles.body} id="google-maps-container">
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "85vh" }}
          center={center}
          zoom={zoom}
          onLoad={(map) => handleLoadMap(map)}
          onUnmount={() => handleUnmountMap()}
          options={{
            fullscreenControl: false,
            scaleControl: true,
            mapTypeControl: true,
            tilt: 0,
            minZoom: GOOGLE_MAPS_MIN_ZOOM,
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }],
              },
            ],
            mapTypeControlOptions: {
              position: GOOGLE_MAPS_BOTTOM_LEFT,
              style: GOOGLE_MAPS_HORIZONTAL_BAR,
              mapTypeIds: [GOOGLE_MAPS_ROAD_MAP, GOOGLE_MAPS_SATELLITE],
            },
            restriction: {
              latLngBounds: {
                east: 180,
                north: 85,
                south: -85,
                west: -180,
              },
              strictBounds: true,
            },
          }}
        >
          {props.geofenceEnabled && props.geofencePoints.length > 0 && (
            <Polygon
              options={{
                paths: props.geofencePoints.map((geofencePoint) => {
                  return { lat: geofencePoint.latitude, lng: geofencePoint.longitude };
                }),
                strokeColor: "#3957d9",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: "#3957d9",
                fillOpacity: 0.35,
              }}
            />
          )}
          {assets.map((asset) => (
            <Marker
              key={asset.assetId}
              position={{ lat: asset.latitude, lng: asset.longitude }}
              title={asset.name}
              onLoad={(marker) => handleMarkerLoad(marker, asset)}
              onClick={() => selectAsset(asset)}
              icon={
                isoUtcIsStale(asset.lastComm)
                  ? {
                      url: "/mapMarkers/mapMarkerGearStale.png",
                      size: new google.maps.Size(40, 48),
                      origin: new google.maps.Point(0, 0),
                      anchor: new google.maps.Point(20, 48),
                      scaledSize: new google.maps.Size(40, 48),
                    }
                  : {
                      url: "/mapMarkers/mapMarkerGearActive.png",
                      size: new google.maps.Size(40, 48),
                      origin: new google.maps.Point(0, 0),
                      anchor: new google.maps.Point(20, 48),
                      scaledSize: new google.maps.Size(40, 48),
                    }
              }
            />
          ))}
          {selectedAsset !== null && (
            <InfoWindow anchor={markerMap[selectedAsset.assetId]} onCloseClick={() => setSelectedAsset(null)}>
              <div className={styles.infoBody}>
                <div className={styles.infoHeader}>{selectedAsset.name}</div>
                <div>
                  Device Type: <span>{selectedAsset.deviceType === null ? "None" : selectedAsset.deviceType}</span>
                </div>
                {selectedAsset.deviceType !== null && <div>Device Identifier: {selectedAsset.deviceIdentifier}</div>}
                <div>Latitude: {selectedAsset.latitude}</div>
                <div>Longitude: {selectedAsset.longitude}</div>
                <div className="my-2">
                  <a href={`/asset/${selectedAsset.assetId}/data`}>View Asset Details</a>
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      )}
      {!props.loading && <MapAssetAlertBanner assets={props.assets} errorMessage={props.errorMessage} />}
    </div>
  );
}

GroupMap.propTypes = {
  assets: PropTypes.array.isRequired,
  geofence: PropTypes.object,
  errorMessage: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
};

interface Props {
  assets: MapAsset[];
  geofenceEnabled: boolean;
  geofencePoints: GeofencePoint[];
  errorMessage: string;
  loading: boolean;
}

interface GeofencePoint {
  geofencePointId: number;
  latitude: number;
  longitude: number;
  draggable?: boolean;
}

interface ShortPoint {
  lat: number;
  lng: number;
}

interface MapAsset {
  assetId: number;
  name: string;
  nickname: string;
  deviceType: string | null;
  deviceIdentifier: string | null;
  latitude: number | null;
  longitude: number | null;
  lastComm: string | null;
}

interface VerifiedMapAsset {
  assetId: number;
  name: string;
  nickname: string;
  deviceType: string | null;
  deviceIdentifier: string | null;
  latitude: number;
  longitude: number;
  lastComm: string | null;
}

interface MarkerMap {
  [key: string]: google.maps.Marker;
}
